import api from './api';
import { ApiResponse } from './user';

export interface Role {
  id: number;
  name: string;
  display_name?: string;
  permissions: Array<{
    id: number;
    name: string;
  }>;
}

export interface Permission {
  id: number;
  name: string;
}

export interface CreateRoleRequest {
  name: string;
  display_name?: string;
  permissions?: string[];
}

export interface UpdateRoleRequest {
  name?: string;
  display_name?: string;
  permissions?: string[];
}

class RoleService {
  // Get all roles
  async getRoles(): Promise<ApiResponse<Role[]>> {
    const response = await api.get('/roles');
    return response.data;
  }

  // Get role by ID
  async getRole(id: number): Promise<ApiResponse<Role>> {
    const response = await api.get(`/roles/${id}`);
    return response.data;
  }

  // Create role
  async createRole(roleData: CreateRoleRequest): Promise<ApiResponse<Role>> {
    const response = await api.post('/roles', roleData);
    return response.data;
  }

  // Update role
  async updateRole(id: number, roleData: UpdateRoleRequest): Promise<ApiResponse<Role>> {
    const response = await api.put(`/roles/${id}`, roleData);
    return response.data;
  }

  // Delete role
  async deleteRole(id: number): Promise<ApiResponse<null>> {
    const response = await api.delete(`/roles/${id}`);
    return response.data;
  }

  // Assign permissions to role
  async assignPermissions(id: number, permissions: string[]): Promise<ApiResponse<Role>> {
    const response = await api.post(`/roles/${id}/assign-permissions`, { permissions });
    return response.data;
  }

  // Get users with specific role
  async getRoleUsers(id: number): Promise<ApiResponse<any[]>> {
    const response = await api.get(`/roles/${id}/users`);
    return response.data;
  }

  // Get all permissions
  async getPermissions(): Promise<ApiResponse<Permission[]>> {
    const response = await api.get('/permissions');
    return response.data;
  }

  // Create permission
  async createPermission(permissionData: { name: string }): Promise<ApiResponse<Permission>> {
    const response = await api.post('/permissions', permissionData);
    return response.data;
  }

  // Update permission
  async updatePermission(id: number, permissionData: { name: string }): Promise<ApiResponse<Permission>> {
    const response = await api.put(`/permissions/${id}`, permissionData);
    return response.data;
  }

  // Delete permission
  async deletePermission(id: number): Promise<ApiResponse<null>> {
    const response = await api.delete(`/permissions/${id}`);
    return response.data;
  }

  // Export all roles to JSON (as blob)
  async exportRoles(): Promise<{ blob: Blob; filename: string }> {
    const response = await api.get('/roles-export', { responseType: 'blob' });
    const disposition = (response.headers as any)['content-disposition'] as string | undefined;
    let filename = 'roles_export.json';
    if (disposition) {
      const match = /filename\*=UTF-8''([^;]+)|filename="?([^;"]+)"?/i.exec(disposition);
      const raw = decodeURIComponent(match?.[1] || match?.[2] || '');
      if (raw) filename = raw;
    }
    return { blob: response.data as Blob, filename };
  }

  // Import roles from JSON file
  async importRoles(file: File | Blob): Promise<ApiResponse<any>> {
    const formData = new FormData();
    formData.append('file', file);
    const response = await api.post('/roles-import', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }
}

const roleService = new RoleService();
export default roleService;
